import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_PROJECTS } from '@/lib/constants/api'
import updateStatus from '@/store/helpers/updateStatus'
import createPaginatedGetAction from '@/store/helpers/createPaginatedGetAction'
import { notifyError, notifySuccess } from '@/lib/notifications'
import buildInitialState from '@/store/helpers/buildInitialState'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'
import getProjectModel from '@/store/modules/models/project'

const getDefaultProjectModel = () => getProjectModel()

const projects = {
  namespaced: true,
  state: buildInitialState({
    projectsList: [],
    totalRecords: 0,
    ...getProjectModel()
  }),
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    setProjectsList: (state, projectsList) => {
      state.projectsList = projectsList
    },
    setPaginatedProjects (state, { result, totalRecords }) {
      state.projectsList = result
      state.totalRecords = totalRecords
    },
    setProject (state, project) {
      state.id = project.id
      state.projectName = project.projectName
      state.clientId = project.clientId
      state.clientName = project.clientName
      state.statusId = project.statusId
      state.status = project.status
      state.createAt = project.createAt
      state.createdBy = project.createdBy
      state.updatedAt = project.updatedAt
      state.updatedBy = project.updatedBy
      state.internalProject = project.internalProject
      state.anticipatedRevenue = project.anticipatedRevenue
      state.projectAssignments = project.projectAssignments || []
    },
    clearProject (state) {
      Object.assign(state, {
        projectsList: state.projectsList,
        ...getDefaultProjectModel()
      })
    }
  },
  actions: {
    getProjectsWithFiltersPaginated ({ dispatch }, paginationParams = {}) {
      return dispatch('getProjectsPaginated', paginationParams)
    },
    getProjectsPaginated: createPaginatedGetAction(PATH_PROJECTS, 'setPaginatedProjects', 'Unable to select projects'),
    getProject: ({ commit, dispatch }, { id }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Getting project, please wait...' })

      return api.doGet({ path: `${PATH_PROJECTS}/${id}`, parameters: { id } })
        .then(json => json.data)
        .then(data => Array.isArray(data) ? data[0] : data)
        .then(project => {
          commit('updateStatus', { status: COMPLETE })
          commit('setProject', project)
          return project
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to select project')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    getProjects: ({ commit, dispatch }, filters = {}) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Getting projects, please wait...' })

      return api.doGet({ path: PATH_PROJECTS, parameters: filters })
        .then(json => json.data)
        .then(projectsList => {
          commit('updateStatus', { status: COMPLETE })
          commit('setProjectsList', projectsList)
          return projectsList
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to select projects')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    getProjectOptions ({ dispatch }, filters = {}) {
      return api.doGet({ path: PATH_PROJECTS, parameters: filters })
        .then(json => json.data || [])
        .catch(() => {
          notifyError(dispatch, 'Unable to load projects')
          return []
        })
    },
    saveProject ({ commit, dispatch }, project) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Saving project, please wait...' })
      return api.doPost({ path: PATH_PROJECTS, data: project })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to save project')
          }

          notifySuccess(dispatch, 'Project saved')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to save project')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    updateProject ({ commit, dispatch }, project) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Updating project, please wait...' })
      return api.doPut({ path: PATH_PROJECTS, data: project })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to update project')
          }

          notifySuccess(dispatch, 'Project updated')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to update project')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    deleteProject ({ commit, dispatch }, id = null) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Deleting project, please wait...' })
      return api.doDelete({
        path: `${PATH_PROJECTS}/${id}`
      })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            if (response.errorMessage) {
              throw new Error(response.errorMessage)
            }
            throw new Error('Unable to delete project')
          }

          notifySuccess(dispatch, 'Project deleted')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, error.message || 'Unable to delete project')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    // Deletes a single assignment from a project by assignmentId
    deleteProjectAssignment ({ commit, dispatch }, assignmentId) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Deleting contract, please wait...' })
      return api.doDelete({ path: `${PATH_PROJECTS}/assignment/${assignmentId}` })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (response.data == null) {
            throw new Error('Unable to delete contract')
          }

          if (response.data > 0) {
            notifySuccess(dispatch, 'Contract deleted')
          }
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to delete contract')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    // Get the anticipated revenue for an assignment
    getAnticipatedRevenue ({ commit, dispatch },
      {
        billRateTypeId,
        billRate,
        workedHoursAverage,
        endDate
      }
    ) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Calculating anticipated revenue, please wait...' })
      const parameters = {
        billRateTypeId: billRateTypeId,
        billRate: billRate,
        workedHoursAverage: workedHoursAverage,
        endDate: endDate
      }
      return api.doGet({ path: `${PATH_PROJECTS}/anticipated-revenue`, parameters: parameters })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (response.data == null) {
            throw new Error('Unable to calculate anticipated revenue')
          }

          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to calculate anticipated revenue')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    // Get the anticipated revenue for a project
    getProjectAnticipatedRevenue ({ commit, dispatch }, assignments) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Calculating anticipated revenue, please wait...' })
      return api.doPost({ path: `${PATH_PROJECTS}/anticipated-revenue/project`, data: assignments })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (response.data == null) {
            throw new Error('Unable to calculate project anticipated revenue')
          }

          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to calculate project anticipated revenue')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    clearProject ({ commit }) {
      commit('clearProject')
    }
  }
}

export default projects
